<template>
  <div class="btc-tmpl__container">
    <div class="btc-tmpl__form">
      <img :src="payload.logo" class="btc-tmpl__logo" />

      <h3 class="btc-tmpl__header">{{ `${$t('btc__invoice')} : ${refId}` }}</h3>
      <p v-if="flowDescr" class="btc-tmpl__descr">{{ flowDescr }}</p>

      <div class="btc-tmpl__timer-block">
        <div class="label">{{ $t('btc__time_remaining') }}:</div>
        <div :class="{ danger: dangerTime }" class="timer">
          {{ timeRemaining }}
        </div>
      </div>

      <div class="btc-tmpl__qr-block">
        <QrcodeVue :value="payload.payment_address" level="H" size="200" />
      </div>

      <div :class="{ center: !diffCurrencies }" class="btc-tmpl__amount-container">
        <div class="btc-tmpl__amount">
          <div class="label">{{ $t('btc__amount') }}</div>
          <div class="amount">
            {{ formatAmount(amount, currency, true) }}
          </div>
        </div>
        <div v-if="diffCurrencies" class="btc-tmpl__amount-to-pay">
          <div class="label">
            {{ $t('btc__amount_to_pay') }}
            <img src="./assets/btc_info.svg" />
            <div class="rate">
              <h4 class="rate__currencies">
                {{
                  $t('btc__exchange_rate', {
                    from: currency,
                    to: currencyToPay,
                  })
                }}
              </h4>
              <p class="rate__value">
                {{ `${payload.exchange_rate} ${currencyToPay}` }}
              </p>
            </div>
          </div>
          <div class="amount">
            {{ formatAmount(amountToPay, currencyToPay) }}
          </div>
        </div>
      </div>

      <div class="btc-tmpl__address-container">
        <p class="label">
          {{
            $t('btc__network_address', {
              currency: diffCurrencies ? currencyToPay : currency,
              network: payload.network,
            })
          }}
        </p>
        <div class="btc-tmpl__address-input">
          <input :value="paymentAddress" type="text" class="input" disabled />
          <div class="copy__icon" @click="copyAddress">
            <img src=",/assets/btc_copy.svg" />
          </div>
          <div class="copy__tooltip">
            {{ $t('btc__copy') }}
          </div>
        </div>
      </div>

      <div v-if="hasReturnUrl" class="btc-tmpl__return" @click="handleReturn">
        <img src="./assets/btc_arrow.svg" />
        {{ $t('btc__return_to_site') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import { MoneyFormatter } from '../../../helpers/Formatting/MoneyFormatter';
import cloneDeep from 'lodash/cloneDeep';
export default {
  components: {
    QrcodeVue,
  },

  created() {
    this.startTimer();
  },

  data() {
    return {
      timer: null,
      timeRemaining: '00:00:00',
      dangerTime: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData']),

    invoiceData() {
      return this.paymentInvoiceData?.data || {};
    },

    payload() {
      const { requisites } = cloneDeep(this.activePayment.payload);

      let data = {
        service_amount: null,
        service_currency: null,
        payment_address: null,
        exchange_rate: null,
        expires_at: null,
        network: null,
        logo: null,
      };

      if (requisites && typeof requisites === 'object') {
        Object.keys(data).forEach(key => {
          if (key in requisites) {
            data[key] = requisites[key];
          }
        });
      }

      Object.keys(data).forEach(key => {
        if (!data[key]) delete data[key];
      });

      return data;
    },

    activePayment() {
      return this.invoiceData.active_payment;
    },

    refId() {
      return this.invoiceData.reference_id;
    },

    flowDescr() {
      return this.invoiceData.description;
    },

    amount() {
      return this.activePayment.amount;
    },

    currency() {
      return this.activePayment.currency;
    },

    diffCurrencies() {
      return this.currency !== this.currencyToPay;
    },

    amountToPay() {
      return this.payload.service_amount || this.invoiceData.payment_amount;
    },

    currencyToPay() {
      return this.payload.service_currency || this.invoiceData.service_currency;
    },

    hasReturnUrl() {
      return this.invoiceData.has_return_url;
    },

    paymentAddress() {
      let { payment_address: address } = this.payload;
      return address.length > 54 ? `${address.slice(0, 54)}...` : address;
    },
  },

  methods: {
    formatAmount(amount, currency, showCurrency) {
      const formatter = new MoneyFormatter();
      return formatter.format(amount, currency, showCurrency);
    },

    startTimer() {
      this.timer = setInterval(() => {
        const currentTime = new Date();
        const expiresTime = new Date(this.payload.expires_at * 1000);
        if (expiresTime > currentTime) {
          const diff = expiresTime - currentTime;
          const _second = 1000;
          const _minute = _second * 60;
          const _hour = _minute * 60;
          const _day = _hour * 24;

          const seconds = this.leadingZero(Math.floor((diff % _minute) / _second));
          const minutes = this.leadingZero(Math.floor((diff % _hour) / _minute));
          const hours = this.leadingZero(Math.floor((diff % _day) / _hour));

          if (minutes < 5) this.dangerTime = true;

          this.timeRemaining = `${hours}:${minutes}:${seconds}`;
        } else {
          clearInterval(this.timer);
        }
      }, 1000);
    },

    leadingZero(number) {
      if (number < 10) {
        return '0' + number;
      } else {
        return number;
      }
    },

    copyAddress() {
      this.$copyText(this.payload.payment_address);
    },

    handleReturn() {
      location.replace(`/return?id=${this.invoiceData.id}&force=true`);
    },
  },
};
</script>
<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

.typography_reg {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;

  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 18px;
  }
}

.typography_bold {
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 1px;

  @media (max-width: 576px) {
    font-size: 18px;
    line-height: 26px;
  }
}

.typography_small {
  letter-spacing: 0.5px;
  font-size: 12px;
  line-height: 16px;
}

.label {
  @extend .typography_reg;
  font-weight: 500;
  color: #7f8fa4;
  margin: 0;
}

.value {
  @extend .typography_bold;
  font-weight: bold;
  color: #334150;
}

.content {
  display: flex;
  flex-direction: column;
}

.rate {
  width: 256px;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 0 50px 0 rgba(50, 74, 101, 0.15);
  top: 25px;
  left: -120px;
  opacity: 0;
  z-index: -1;
  transition: 0.2s;

  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #ffffff;
    position: absolute;
    top: -5px;
    right: 20px;
  }

  &__currencies {
    @extend .typography_small;
    margin: 0 0 8px;
    font-weight: 500;
    color: #334150;
  }

  &__value {
    @extend .typography_small;
    margin: 0;
    color: #334150;
    font-weight: 400;
  }
}

.btc-tmpl {
  &__container {
    position: absolute;
    top: 0;
    bottom: 0;
    right: -20px;
    left: -20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f8f9fc;
    font-family: 'Roboto', sans-serif;
  }

  &__form {
    width: 560px;
    min-height: 640px;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 27px 42px;
    box-shadow: 0px 50px 60px -30px rgba(50, 74, 101, 0.15);
    border-radius: 10px;
    background-color: #ffffff;

    @media (max-width: 576px) {
      width: 90%;
      padding: 27px 20px;
    }
  }

  &__logo {
    height: 94px;
    margin-bottom: 12px;
  }

  &__header {
    @extend .typography_bold;
    margin: 0;
    font-weight: bold;
    color: #334150;
    word-break: break-all;
  }

  &__descr {
    @extend .typography_reg;
    margin: 10px 0 0;
    color: #7f8fa4;
  }

  &__timer-block {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 12px 0;

    .timer {
      @extend .value;
      margin-left: 8px;

      &.danger {
        color: #e82a41;
      }
    }
  }

  &__qr-block {
    margin-bottom: 12px;
  }

  &__amount-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    @media (max-width: 476px) {
      flex-direction: column;
      align-items: center;
    }

    &.center {
      justify-content: center;

      .btc-tmpl__amount {
        align-items: center;
      }
    }
  }

  &__amount {
    @extend .content;
    align-items: flex-start;

    @media (max-width: 476px) {
      align-items: center;
    }

    .amount {
      margin-top: 4px;
      @extend .value;
    }
  }

  &__amount-to-pay {
    @extend .content;
    align-items: flex-end;

    @media (max-width: 476px) {
      align-items: center;
      margin-top: 12px;
    }

    .label {
      display: flex;
      align-items: center;
      position: relative;

      img {
        margin-left: 6px;
        cursor: pointer;

        &:hover + .rate {
          opacity: 1;
          z-index: 1;
        }
      }
    }

    .amount {
      margin-top: 4px;
      @extend .value;
    }
  }

  &__address-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 16px;
  }

  &__address-input {
    display: flex;
    padding-right: 40px;
    width: 100%;
    border: 1px solid #e1e8ee;
    box-sizing: border-box;
    border-radius: 6px;
    position: relative;
    margin: 6px 0 0;

    .input {
      @extend .typography_reg;
      width: 100%;
      padding: 10px 0 10px 16px;
      color: #334150;
      background-color: transparent;
      border: none;
    }

    .copy {
      &__icon {
        width: 24px;
        height: 24px;
        background: #f8f9fc;
        border-radius: 6px;
        position: absolute;
        right: 8px;
        top: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &:hover + .copy__tooltip {
          opacity: 1;
          z-index: 1;
        }
      }

      &__tooltip {
        @extend .typography_small;
        position: absolute;
        padding: 8px;
        background: #334150;
        border-radius: 6px;
        color: #ffffff;
        top: -30px;
        right: 0;
        opacity: 0;
        z-index: -1;
        transition: 0.2s;

        &:after {
          content: '';
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid #334150;
          position: absolute;

          bottom: -5px;
          left: 18px;
        }
      }
    }
  }

  &__return {
    @extend .typography_reg;
    width: 100%;
    margin-top: 32px;
    display: flex;
    color: #0020ff;
    font-weight: 500;
    cursor: pointer;

    img {
      margin-right: 10px;
    }
  }
}
</style>
